import { AlertCircle, AlertTriangle, CheckCircle, Info } from "lucide-react";
import React, { forwardRef, useState, useRef, useLayoutEffect } from "react";
import { cn } from "../../utils/cn";
import Tooltip from "./Tooltip";

interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  labelRight?: React.ReactNode;
  error?: string;
  success?: string;
  hint?: string;
  /** Aviso não bloqueante (ex: acima do limite recomendado de exibição, mas ainda dentro do limite técnico). */
  warning?: string;
  variant?: "default" | "filled" | "outline";
  inputSize?: "sm" | "md" | "lg";
  autoResize?: boolean;
  maxHeight?: number;
}

const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      className,
      label,
      labelRight,
      error,
      success,
      hint,
      warning,
      variant = "default",
      inputSize = "md",
      id,
      autoResize = false,
      maxHeight,
      ...props
    },
    ref,
  ) => {
    const [isFocused, setIsFocused] = useState(false);
    const internalRef = useRef<HTMLTextAreaElement | null>(null);

    const setRef = (element: HTMLTextAreaElement | null) => {
      internalRef.current = element;
      if (typeof ref === "function") {
        ref(element);
      } else if (ref) {
        ref.current = element;
      }
    };

    const adjustHeight = () => {
      if (autoResize && internalRef.current) {
        const el = internalRef.current;
        const offset = el.offsetHeight - el.clientHeight; // Compensa a borda (box-sizing)

        el.style.height = "auto"; // Reseta para calcular real height
        const newHeight = el.scrollHeight + offset;

        if (maxHeight && newHeight > maxHeight) {
          el.style.height = `${maxHeight}px`;
          el.style.overflowY = "auto";
        } else {
          el.style.height = `${newHeight}px`;
          el.style.overflowY = "hidden";
        }
      }
    };

    useLayoutEffect(() => {
      adjustHeight();
    }, [props.value, autoResize, maxHeight]);

    const handleInput: React.FormEventHandler<HTMLTextAreaElement> = (e) => {
      adjustHeight();
      // Cast pontual: há duas versões de @types/react no package.json (18.x
      // e 19.x) e a tipagem de `onInput` resolvida aqui diverge de
      // `FormEvent` — não é um bug de runtime, é só o conflito de versões
      // de tipos. Fora do escopo consertar isso aqui.
      (
        props.onInput as React.FormEventHandler<HTMLTextAreaElement> | undefined
      )?.(e);
    };

    const textareaId =
      id || `textarea-${Math.random().toString(36).substr(2, 9)}`;

    const sizeClasses = {
      sm: "py-1.5 sm:py-2 px-3 text-xs sm:text-sm min-h-[80px]",
      md: "py-2 sm:py-2.5 md:py-3 px-3.5 sm:px-4 md:px-4 text-sm sm:text-sm md:text-base min-h-[120px] sm:min-h-[140px] md:min-h-[150px]",
      lg: "py-2.5 sm:py-3 md:py-4 px-4 sm:px-4.5 md:px-5 text-base sm:text-base md:text-lg min-h-[160px] sm:min-h-[180px] md:min-h-[200px]",
    };

    const variantClasses = {
      default: "border border-purple-300 bg-white",
      filled: "border-0 bg-gray-100 focus:bg-white",
      outline: "border-2 border-purple-300 bg-transparent",
    };

    const baseTextareaClasses = [
      "w-full rounded-lg transition-colors duration-200",
      "focus:outline-hidden focus:ring-2 focus:ring-purple-500/20",
      "disabled:opacity-50 disabled:cursor-not-allowed",
      "placeholder:text-gray-500",
      "text-gray-900",
      "resize-y",
    ].join(" ");

    const stateClasses = error
      ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
      : success
        ? "border-green-300 focus:border-green-500 focus:ring-green-500/20"
        : isFocused
          ? "border-purple-400 focus:border-purple-500"
          : variant === "default"
            ? "hover:border-purple-400"
            : "";

    return (
      <div className="space-y-1.5 sm:space-y-2">
        {/* Label */}
        {label && (
          <div className="flex items-center justify-between gap-2 mb-1 sm:mb-1.5">
            <label
              htmlFor={textareaId}
              className="flex items-center gap-1.5 text-xs sm:text-sm font-medium text-gray-700 min-w-0"
            >
              <span className="truncate min-w-0">
                {label}
                {props.required && <span className="text-red-500 ml-1">*</span>}
              </span>
              {hint && (
                <Tooltip content={hint}>
                  <Info className="h-3.5 w-3.5 text-gray-400 hover:text-gray-600 transition-colors cursor-help shrink-0" />
                </Tooltip>
              )}
            </label>
            {labelRight && (
              <div className="shrink-0 leading-none">{labelRight}</div>
            )}
          </div>
        )}

        {/* Textarea Container */}
        <div className="relative">
          {/* Textarea */}
          <textarea
            ref={setRef}
            id={textareaId}
            className={cn(
              baseTextareaClasses,
              sizeClasses[inputSize],
              variantClasses[variant],
              stateClasses,
              className,
            )}
            onInput={handleInput}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            {...props}
          />

          {/* Status Icons */}
          {(error || success) && (
            <div className="absolute top-3 right-3 pointer-events-none">
              {error ? (
                <AlertCircle className="h-5 w-5 text-red-500" />
              ) : success ? (
                <CheckCircle className="h-5 w-5 text-green-500" />
              ) : null}
            </div>
          )}
        </div>

        {/* Helper Text */}
        {(error || success || warning) && (
          <div className="space-y-1">
            {error && (
              <p className="flex items-center text-sm text-red-600">
                <AlertCircle className="h-4 w-4 mr-1 shrink-0" />
                {error}
              </p>
            )}
            {success && (
              <p className="flex items-center text-sm text-green-600">
                <CheckCircle className="h-4 w-4 mr-1 shrink-0" />
                {success}
              </p>
            )}
            {warning && !error && !success && (
              <p className="flex items-center text-xs text-amber-600">
                <AlertTriangle className="h-3.5 w-3.5 mr-1 shrink-0" />
                {warning}
              </p>
            )}
          </div>
        )}
      </div>
    );
  },
);

Textarea.displayName = "Textarea";

export default Textarea;
